/**
 * useNicheFinderFilters — estado dos filtros + sync com URL.
 *
 * Sprint 3 v4: shape novo dos filtros.
 *   Inline (aplicam em tempo real):
 *     search, sortBy
 *   Modal de filtros avançados (batch via setFilters):
 *     languages[], categories[],
 *     subsMin/subsMax, viewsMin/viewsMax,
 *     monetized (true|false|null)
 *
 * URL params:
 *   ?search=foo&sort=most_views
 *   &lang=pt-BR,en&cat=Crypto,History
 *   &subs_min=1000&subs_max=10000
 *   &views_min=100000&views_max=...
 *   &monetized=1|0
 *
 * Pacote 7.6: filtro Faceless removido (toda a base e faceless). A metrica
 * % FACELESS continua no card; o backend ainda aceita faceless_only
 * (retro-compat), apenas nao recebe mais do frontend.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFilterOptions } from './useFilterOptions';

const DEFAULT_SORT = 'most_views';

function parseListParam(value) {
  if (!value) return [];
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

function serializeListParam(list) {
  return Array.isArray(list) && list.length ? list.join(',') : null;
}

function parseIntOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

function parseTriState(value) {
  // '1' -> true, '0' -> false, ausente -> null
  if (value === '1') return true;
  if (value === '0') return false;
  return null;
}

export const EMPTY_FILTERS = {
  search: '',
  sortBy: DEFAULT_SORT,
  languages: [],
  categories: [],
  subsMin: null,
  subsMax: null,
  viewsMin: null,
  viewsMax: null,
  videosMin: null,
  videosMax: null,
  monetized: null,
  // 'any' | 'last_hour' | 'last_24h' | 'last_7d' | 'last_30d'.
  // 'any' = sem filtro temporal.
  addedAfter: 'any',
};

export function useNicheFinderFilters({ enabled = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => ({
    search: searchParams.get('search') || '',
    sortBy: searchParams.get('sort') || DEFAULT_SORT,
    languages: parseListParam(searchParams.get('lang')),
    categories: parseListParam(searchParams.get('cat')),
    subsMin: parseIntOrNull(searchParams.get('subs_min')),
    subsMax: parseIntOrNull(searchParams.get('subs_max')),
    viewsMin: parseIntOrNull(searchParams.get('views_min')),
    viewsMax: parseIntOrNull(searchParams.get('views_max')),
    videosMin: parseIntOrNull(searchParams.get('videos_min')),
    videosMax: parseIntOrNull(searchParams.get('videos_max')),
    monetized: parseTriState(searchParams.get('monetized')),
    addedAfter: searchParams.get('added') || 'any',
  }), [searchParams]);

  // Helpers serializadores compartilhados (setFilter + setFilters)
  const applyToParams = useCallback((params, key, value) => {
    switch (key) {
      case 'search':
        if (value) params.set('search', value); else params.delete('search');
        break;
      case 'sortBy':
        if (value && value !== DEFAULT_SORT) params.set('sort', value);
        else params.delete('sort');
        break;
      case 'languages': {
        const s = serializeListParam(value);
        if (s) params.set('lang', s); else params.delete('lang');
        break;
      }
      case 'categories': {
        const s = serializeListParam(value);
        if (s) params.set('cat', s); else params.delete('cat');
        break;
      }
      case 'subsMin':
        if (value !== null && value !== undefined && !Number.isNaN(value)) {
          params.set('subs_min', String(value));
        } else params.delete('subs_min');
        break;
      case 'subsMax':
        if (value !== null && value !== undefined && !Number.isNaN(value)) {
          params.set('subs_max', String(value));
        } else params.delete('subs_max');
        break;
      case 'viewsMin':
        if (value !== null && value !== undefined && !Number.isNaN(value)) {
          params.set('views_min', String(value));
        } else params.delete('views_min');
        break;
      case 'viewsMax':
        if (value !== null && value !== undefined && !Number.isNaN(value)) {
          params.set('views_max', String(value));
        } else params.delete('views_max');
        break;
      case 'videosMin':
        if (value !== null && value !== undefined && !Number.isNaN(value)) {
          params.set('videos_min', String(value));
        } else params.delete('videos_min');
        break;
      case 'videosMax':
        if (value !== null && value !== undefined && !Number.isNaN(value)) {
          params.set('videos_max', String(value));
        } else params.delete('videos_max');
        break;
      case 'monetized':
        if (value === true) params.set('monetized', '1');
        else if (value === false) params.set('monetized', '0');
        else params.delete('monetized');
        break;
      case 'addedAfter':
        if (value && value !== 'any') params.set('added', value);
        else params.delete('added');
        break;
      default:
        // ignore unknown key
        break;
    }
  }, []);

  const setFilter = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      applyToParams(next, key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams, applyToParams]);

  /** Batch update — usado pelo modal ao clicar 'Aplicar filtros'. */
  const setFilters = useCallback((partial) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(partial || {}).forEach(([k, v]) => applyToParams(next, k, v));
      return next;
    }, { replace: true });
  }, [setSearchParams, applyToParams]);

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      [
        'search', 'sort', 'lang', 'cat',
        'subs_min', 'subs_max',
        'views_min', 'views_max',
        'videos_min', 'videos_max',
        'monetized',
        'added',
      ].forEach((k) => next.delete(k));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => (
    !!filters.search
    || filters.sortBy !== DEFAULT_SORT
    || filters.languages.length > 0
    || filters.categories.length > 0
    || filters.subsMin !== null
    || filters.subsMax !== null
    || filters.viewsMin !== null
    || filters.viewsMax !== null
    || filters.videosMin !== null
    || filters.videosMax !== null
    || filters.monetized !== null
    || (filters.addedAfter && filters.addedAfter !== 'any')
  ), [filters]);

  /** Conta filtros "avançados" (excluindo search e sort) — usado no
   *  badge do botão "Filtros". */
  const advancedFilterCount = useMemo(() => {
    let n = 0;
    if (filters.languages.length > 0) n += 1;
    if (filters.categories.length > 0) n += 1;
    if (filters.subsMin !== null || filters.subsMax !== null) n += 1;
    if (filters.viewsMin !== null || filters.viewsMax !== null) n += 1;
    if (filters.videosMin !== null || filters.videosMax !== null) n += 1;
    if (filters.monetized !== null) n += 1;
    if (filters.addedAfter && filters.addedAfter !== 'any') n += 1;
    return n;
  }, [filters]);

  // ===== Opcoes de filtro do BANCO INTEIRO (via /filter-options) =====
  // Antes: availableLanguages/availableCategories eram derivados dos canais
  // ja carregados no NicheFinderPage (bug: so ~30-90 canais). Agora vem do
  // endpoint /filter-options (todos os idiomas/categorias do banco).
  const { options: filterOptions } = useFilterOptions({ enabled });
  const availableLanguages = useMemo(
    () => (filterOptions?.languages || []).map((l) => l.value).filter(Boolean),
    [filterOptions],
  );
  const availableCategories = useMemo(
    () => (filterOptions?.categories || []).map((c) => c.value).filter(Boolean),
    [filterOptions],
  );

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    hasActiveFilters,
    advancedFilterCount,
    availableLanguages,
    availableCategories,
    filterOptions,
  };
}
